import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Database, Clock, Save, RotateCcw, Trash2, Plus,
  HardDrive, Download, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

export function BackupsList({
  backups,
  isSuperAdmin,
  formatBytes,
  onCreateClick,
  onDownload,
  onRestore,
  onDelete,
  // Bulk selection (optional — parent wires the handlers + state)
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,
  onBulkDelete,
  // Server-side pagination — falls back to client-only display when the
  // parent hasn't wired the new envelope (backwards compat).
  page = 1,
  pageSize = 25,
  total = null,
  totalPages = null,
  onPageChange,
}) {
  const totalCount = total ?? backups.length;
  const pages = totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));
  const showPager = typeof onPageChange === 'function' && pages > 1;
  const fromIdx = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const toIdx = Math.min(totalCount, page * pageSize);
  const bulkEnabled = typeof onToggleSelect === 'function';
  const allOnPageSelected = bulkEnabled
    && backups.length > 0
    && backups.every(b => selectedIds.includes(b.id));
  const someOnPageSelected = bulkEnabled
    && backups.some(b => selectedIds.includes(b.id));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              Available Backups
            </CardTitle>
            <CardDescription>
              {totalCount === 0
                ? '0 backups'
                : `${fromIdx}–${toIdx} of ${totalCount} backup(s)`}
              {bulkEnabled && selectedIds.length > 0 && (
                <span className="ml-2 text-primary font-medium">
                  · {selectedIds.length} selected
                </span>
              )}
            </CardDescription>
          </div>
          {bulkEnabled && isSuperAdmin && selectedIds.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onBulkDelete}
              data-testid="btn-bulk-delete-backups"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected ({selectedIds.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {backups.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  {bulkEnabled && isSuperAdmin && (
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        ref={el => { if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected; }}
                        onChange={() => onToggleSelectAll && onToggleSelectAll(backups.map(b => b.id))}
                        aria-label="Select all on page"
                        data-testid="checkbox-select-all-backups"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Size</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Collections</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {backups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-muted/30">
                    {bulkEnabled && isSuperAdmin && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(backup.id)}
                          onChange={() => onToggleSelect(backup.id)}
                          aria-label={`Select backup ${backup.name}`}
                          data-testid={`checkbox-backup-${backup.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{backup.name}</p>
                        {backup.description && (
                          <p className="text-xs text-muted-foreground">{backup.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-mono text-sm">{format(new Date(backup.created_at), 'dd/MM/yyyy HH:mm')}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(backup.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      <p data-testid={`backup-size-${backup.id}`}>{formatBytes(backup.size_bytes)}</p>
                      {backup.uncompressed_size_bytes > 0 && (
                        <p
                          className="text-xs text-muted-foreground font-normal"
                          data-testid={`backup-size-uncompressed-${backup.id}`}
                        >
                          {formatBytes(backup.uncompressed_size_bytes)} uncompressed
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {backup.is_auto ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Auto
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Save className="w-3 h-3 mr-1" />
                          Manual
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-muted-foreground">
                        {backup.collections_backed_up?.length || 0} collections
                      </p>
                      {backup.total_documents > 0 && (
                        <p
                          className="text-xs text-muted-foreground"
                          data-testid={`backup-doc-count-${backup.id}`}
                        >
                          {backup.total_documents.toLocaleString()} docs
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isSuperAdmin && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDownload(backup)}
                            title="Download as file"
                            data-testid={`btn-download-backup-${backup.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRestore(backup)}
                            title="Restore from this backup"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(backup)}
                            title="Delete backup"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {showPager && (
              <div className="flex items-center justify-between border-t px-4 py-3 bg-muted/30">
                <div className="text-xs text-muted-foreground">
                  Page {page} of {pages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    data-testid="backups-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pages}
                    data-testid="backups-next-page"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No backups yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first backup to protect your data
            </p>
            {isSuperAdmin && (
              <Button onClick={onCreateClick}>
                <Plus className="w-4 h-4 mr-2" />
                Create Backup
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
